import { cloneElement, isValidElement, useContext, useEffect, useRef } from 'react'
import { HelpCircle } from 'lucide-react'
import { cn, nextId } from '../internal/utils'
import { pathKey, toPath } from '../internal/formStore'
import Tooltip from '../Tooltip'
import { FieldPrefixContext, useFieldState } from './context'

/** antd default: read the value from `event.target.value` (or `.checked`). */
const defaultGetValueFromEvent = (valuePropName, ...args) => {
  const [first] = args
  if (first && typeof first === 'object' && 'target' in first && first.target) {
    return valuePropName === 'checked' ? first.target.checked : first.target.value
  }
  return first
}

const isRequired = (rules, required) =>
  required || (Array.isArray(rules) && rules.some((rule) => rule && rule.required))

/**
 * antd Form.Item. Handles `name`, `label`, `rules`, `dependencies`, `shouldUpdate`,
 * `valuePropName`, `getValueFromEvent`, `normalize`, `noStyle`, `hidden`,
 * `validateStatus`/`help` (controlled display), `extra`, `tooltip` and children
 * as a render function.
 */
const FormItem = ({
  name,
  label,
  rules,
  required,
  dependencies,
  shouldUpdate,
  valuePropName = 'value',
  getValueFromEvent,
  getValueProps,
  normalize,
  initialValue,
  noStyle = false,
  hidden = false,
  help,
  validateStatus,
  extra,
  tooltip,
  colon = true,
  labelCol,
  wrapperCol,
  preserve,
  trigger = 'onChange',
  validateTrigger = 'onChange',
  className,
  style,
  children,
  ...rest
}) => {
  // `shouldUpdate` and dependency-only items subscribe to everything, as antd does.
  const watchAll = Boolean(shouldUpdate) || (name == null && Boolean(dependencies))
  const { context, fullName, key } = useFieldState(name, watchAll)
  const prefix = useContext(FieldPrefixContext)
  const controlId = useRef(nextId('field')).current
  const form = context?.form

  useEffect(() => {
    if (!form || !fullName) return undefined
    return form.registerField(fullName, {
      rules,
      dependencies: dependencies?.map((dependency) => [...prefix, ...toPath(dependency)]),
      label: typeof label === 'string' ? label : undefined,
      initialValue,
      preserve: preserve ?? context?.preserve,
    })
    // `rules` is often an inline array; comparing by key keeps this from looping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, key, JSON.stringify(rules?.map((rule) => (typeof rule === 'function' ? 'fn' : rule)) ?? [])])

  const errors = form && fullName ? form.getFieldError(fullName) : []
  const resolvedStatus = validateStatus ?? (errors.length > 0 ? 'error' : undefined)
  const resolvedHelp = help ?? (errors.length > 0 ? errors[0] : undefined)
  const helpId = resolvedHelp ? `${controlId}-help` : undefined

  const renderControl = () => {
    if (typeof children === 'function') {
      return children(form, { getFieldValue: form?.getFieldValue })
    }
    if (!fullName || !form || !isValidElement(children)) return children

    const value = form.getFieldValue(fullName)
    const valueProps = getValueProps
      ? getValueProps(value)
      : { [valuePropName]: valuePropName === 'checked' ? Boolean(value) : value }

    const handler = (...args) => {
      const raw = getValueFromEvent
        ? getValueFromEvent(...args)
        : defaultGetValueFromEvent(valuePropName, ...args)
      const next = normalize ? normalize(raw, value, form.getFieldsValue()) : raw
      form.setFieldValue(fullName, next)
      children.props[trigger]?.(...args)
      if (validateTrigger) form.validateField(fullName)
    }

    return cloneElement(children, {
      ...valueProps,
      [trigger]: handler,
      id: children.props.id ?? controlId,
      status: children.props.status ?? (resolvedStatus === 'error' ? 'error' : undefined),
      'aria-invalid': resolvedStatus === 'error' || undefined,
      'aria-describedby': helpId ?? children.props['aria-describedby'],
      'aria-required': isRequired(rules, required) || undefined,
      disabled: children.props.disabled ?? context?.disabled,
    })
  }

  const control = renderControl()

  if (noStyle) return control

  return (
    <div
      className={cn(
        'ui-form-item mb-4',
        context?.layout === 'horizontal' && 'sm:flex sm:items-start sm:gap-3',
        context?.layout === 'inline' && 'mb-0 inline-flex items-start gap-2',
        resolvedStatus === 'error' && 'ui-form-item-has-error',
        hidden && 'hidden',
        className
      )}
      style={style}
      {...rest}
    >
      {label != null ? (
        <div
          className={cn(
            'ui-form-item-label',
            context?.layout === 'horizontal' ? 'sm:w-1/3 sm:pt-2.5 sm:text-right' : 'mb-1.5'
          )}
          style={labelCol?.style}
        >
          <label
            htmlFor={controlId}
                  className={cn(
                    'inline-flex items-center gap-1 text-sm font-medium text-[var(--text-body)]',
                    isRequired(rules, required) &&
                      "ui-form-item-required before:mr-0.5 before:text-[var(--error-color)] before:content-['*']",
                    // antd draws the colon with CSS; as a pseudo-element it stays
                    // out of the label's text, so `getByLabelText` still matches.
                    colon && context?.layout === 'horizontal' && "after:ml-0.5 after:content-[':']"
                  )}
                >
                  {label}
            {tooltip ? (
              <Tooltip title={tooltip}>
                <span className="inline-flex text-[var(--gray-600)]" aria-hidden="true">
                  <HelpCircle size={13} />
                </span>
              </Tooltip>
            ) : null}
          </label>
        </div>
      ) : null}

      <div
        className={cn('ui-form-item-control min-w-0', context?.layout === 'horizontal' && 'sm:flex-1')}
        style={wrapperCol?.style}
      >
        <div className="ui-form-item-control-input">{control}</div>
        {resolvedHelp ? (
          <div
            id={helpId}
            role={resolvedStatus === 'error' ? 'alert' : undefined}
            className={cn(
              'ui-form-item-explain mt-1 text-xs',
              resolvedStatus === 'error'
                ? 'ui-form-item-explain-error text-[var(--error-color)]'
                : 'text-[var(--text-muted)]'
            )}
          >
            {resolvedHelp}
          </div>
        ) : null}
        {extra ? (
          <div className="ui-form-item-extra mt-1 text-xs text-[var(--text-muted)]">{extra}</div>
        ) : null}
      </div>
    </div>
  )
}

export { defaultGetValueFromEvent, isRequired }
export default FormItem
